import { getPartnerToken, partnerTokenClaims, isPartnerSessionSwitching } from '@/services/auth/partnerSessionStorage';
import axios from 'axios';
import { getOrCreateDeviceId, isValidDeviceId } from '@/services/device/deviceIdentity';
import { getDeviceKey } from '@/services/device/deviceCredential';
import {
  isBackendUnavailableError,
  markBackendAvailable,
  markBackendUnavailable,
} from '@/services/connectivity/connectivityService';
import { isOperationalBlockingError } from '@/services/device/deviceOperationalPolicy';

let interceptorInstalled = false;
let responseInterceptorInstalled = false;
let activeStore = null;
let functionalCloudRequests = 0;
let operationalRequestSequence = 0;

function nextOperationalRequestId() {
  operationalRequestSequence = (operationalRequestSequence + 1) % 1000;
  return (Date.now() * 1000) + operationalRequestSequence;
}

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);
const TECHNICAL_PATHS = [
  '/v1/api/partner/restaurants',
  '/v1/api/partner/select-restaurant',
  '/v1/api/partner/pair',
  '/v1/api/partner/refresh',
  '/v1/api/partner/verify',
  '/v1/api/tpv/',
];
const DEVICE_HEADER_EXCLUDED_PATHS = [
  '/v1/api/app/loginAdmin',
  '/v1/api/app/restaurants',
  '/v1/api/app/users_by_restaurant',
  '/v1/api/app/admin',
  '/v1/api/partner/restaurants',
  '/v1/api/partner/select-restaurant',
  '/v1/api/partner/pair',
  '/v1/api/partner/refresh',
  '/v1/api/partner/verify',
  // Customer QR/order requests are cloud requests. Their authority is
  // decided server-side from the restaurant mode (CLOUD_MODE vs LOCAL_MODE),
  // so a cached partner/device epoch must never be attached here. A stale
  // epoch in a customer browser otherwise produces OPERATION_EPOCH_MISMATCH
  // even though cloud is available and the restaurant is still in cloud mode.
  '/v1/api/public/qr-orders/',
  '/v1/api/app/create_order',
  '/v1/api/app/addShoppingCart',
  '/v1/api/app/addShoppingCartSteps',
  '/v1/api/app/EditOrderShoppingCart',
  '/v1/api/app/ClientUpdateOrder',
  '/v1/api/app/update_delivery',
  '/v1/api/app/update_take_away',
  '/v1/api/app/update_barra_kiosk',
];

// These endpoints are shared by customer QR views and the authenticated TPV.
// Customer calls must stay free of device authority headers, while TPV calls
// need them so the backend can resolve the registered restaurant device.
const SHARED_CUSTOMER_OPERATION_PATHS = [
  '/v1/api/app/addShoppingCart',
  '/v1/api/app/addShoppingCartSteps',
  '/v1/api/app/EditOrderShoppingCart',
  '/v1/api/app/update_delivery',
  '/v1/api/app/update_take_away',
  '/v1/api/app/update_barra_kiosk',
];

// POS confirmation still needs the device identity/key so the server can
// apply the primary/secondary policy. Only the cached epoch is omitted: a
// browser may have kept an old epoch after a Hub/cloud authority transition.
const DEVICE_EPOCH_EXCLUDED_PATHS = [
  '/v1/api/app/createPosOrder',
];

function isCloudApiRequest(config) {
  const cloud = String(process.env.VUE_APP_API_URL || '');
  return Boolean(cloud && String(config?.url || '').startsWith(cloud));
}

function currentPartnerToken() {
  const value = activeStore?.state?.partner_token
    || (typeof localStorage !== 'undefined'
      ? getPartnerToken()
      : null);
  return value && !['null', 'undefined'].includes(String(value))
    ? String(value)
    : null;
}

function isTpvCloudRequest(config) {
  return isCloudApiRequest(config)
    && String(config?.url || '').includes('/v1/api/tpv/');
}

function headerValue(headers, name) {
  if (typeof headers?.get === 'function') return headers.get(name);
  return headers?.[name] || headers?.[name.toLowerCase()] || null;
}

function usesCurrentPartnerAuthentication(config) {
  const partnerToken = currentPartnerToken();
  if (!partnerToken) return false;
  return String(headerValue(config?.headers, 'Authorization') || '').trim()
    === `Bearer ${partnerToken}`;
}

function requestNeedsDeviceHeaders(config) {
  const url = String(config?.url || '');
  if (!isCloudApiRequest(config)) return false;

  const excluded = DEVICE_HEADER_EXCLUDED_PATHS.some((path) => url.includes(path));
  if (!excluded) return true;

  return SHARED_CUSTOMER_OPERATION_PATHS.some((path) => url.includes(path))
    && usesCurrentPartnerAuthentication(config);
}

function setHeader(headers, name, value) {
  if (typeof headers.set === 'function') {
    headers.set(name, value);
  } else {
    headers[name] = value;
  }
}

function clearRecoveredCloudBlock() {
  const reason = activeStore?.state?.device?.operationalAccessReason;
  if (!['HYBRID_CLOUD_UNAVAILABLE', 'SECONDARY_API_UNAVAILABLE'].includes(reason)) {
    return false;
  }
  activeStore.commit('device/clearOperationalBlocking');
  return true;
}

export function createCorrelationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ewu-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isFunctionalCloudRequest(config) {
  const url = String(config.url || '');
  const cloud = String(process.env.VUE_APP_API_URL || '');
  return Boolean(
    cloud
    && url.startsWith(cloud)
    && MUTATING.has(String(config.method || 'get').toLowerCase())
    && !TECHNICAL_PATHS.some((path) => url.includes(path)),
  );
}

function finishFunctionalRequest(config) {
  if (config?.__ewuFunctionalCloud) {
    functionalCloudRequests = Math.max(0, functionalCloudRequests - 1);
  }
}

export async function waitForCloudRequestsToSettle(timeoutMs = 8000) {
  const deadline = Date.now() + timeoutMs;
  while (functionalCloudRequests > 0 && Date.now() < deadline) {
    await new Promise((resolve) => window.setTimeout(resolve, 25));
  }
  return functionalCloudRequests === 0;
}

export function installDeviceHeaderInterceptor() {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  axios.interceptors.request.use(async (config) => {
    const authority = activeStore?.state?.operationalMode;
    if (isCloudApiRequest(config)) {
      config.__ewuOperationalRequestId = nextOperationalRequestId();
    }
    const functional = isFunctionalCloudRequest(config);
    if (isPartnerSessionSwitching() && isCloudApiRequest(config)) {
      const error = new Error('Cambio de restaurante en curso. Espera a que termine.');
      error.code = 'PARTNER_SESSION_SWITCHING';
      error.config = config;
      return Promise.reject(error);
    }
    const capturedToken = String(headerValue(config.headers, 'Authorization') || '').replace(/^Bearer\s+/i, '');
    const capturedClaims = partnerTokenClaims(capturedToken);
    const liveClaims = partnerTokenClaims(currentPartnerToken());
    const capturedScope = capturedClaims.restaurant_id;
    const liveScope = liveClaims.restaurant_id;
    if (isCloudApiRequest(config) && capturedScope !== liveScope && (
      capturedScope || (liveScope && capturedClaims.id === liveClaims.id)
    )) {
      const error = new Error('La sesión ha cambiado. Vuelve a abrir esta sección.');
      error.code = 'PARTNER_SESSION_CHANGED';
      error.config = config;
      return Promise.reject(error);
    }
    if (
      functional
      && (
        ['LOCAL_MODE', 'LOCAL_MODE_PREPARING'].includes(authority?.mode)
        || ['PREPARING_LOCAL'].includes(authority?.state)
      )
    ) {
      const error = new Error('CLOUD_WRITES_FENCED');
      error.code = 'CLOUD_WRITES_FENCED';
      return Promise.reject(error);
    }
    if (functional) {
      functionalCloudRequests += 1;
      config.__ewuFunctionalCloud = true;
    }
    const headers = config.headers || {};
    const correlationId = config.__ewuCorrelationId || createCorrelationId();
    if (
      (typeof headers.has !== 'function' || !headers.has('X-Correlation-ID'))
      && !headers['X-Correlation-ID']
    ) {
      setHeader(headers, 'X-Correlation-ID', correlationId);
    }

    // Hub discovery, pairing and device status can survive a partner switch as
    // background tasks. Never let one of those tasks send the JWT captured for
    // the previous restaurant.
    const livePartnerToken = currentPartnerToken();
    if (livePartnerToken && isTpvCloudRequest(config) && (!capturedScope || capturedScope === liveScope)) {
      setHeader(headers, 'Authorization', `Bearer ${livePartnerToken}`);
    }
    config.headers = headers;
    config.__ewuCorrelationId = correlationId;

    // Authentication and Admin control-plane calls must never depend on local
    // device identity, IndexedDB or an offline credential.
    if (!requestNeedsDeviceHeaders(config)) return config;

    const deviceId = await getOrCreateDeviceId();
    if (!isValidDeviceId(deviceId)) return config;

    setHeader(headers, 'X-EWU-Device-ID', deviceId);
    const deviceKey = await getDeviceKey(deviceId);
    if (deviceKey) setHeader(headers, 'X-EWU-Device-Key', deviceKey);
    if (
      String(config.url || '').startsWith(process.env.VUE_APP_API_URL || '')
      && authority?.operationEpoch
      && authority?.mode !== 'CLOUD_MODE'
      && !DEVICE_EPOCH_EXCLUDED_PATHS.some((path) => String(config.url || '').includes(path))
    ) {
      setHeader(
        headers,
        'X-EWU-Operation-Epoch',
        String(authority.operationEpoch),
      );
    }
    config.headers = headers;
    return config;
  });
}

export function configureDeviceResponseInterceptor(store) {
  activeStore = store || activeStore;
  if (responseInterceptorInstalled) return;
  responseInterceptorInstalled = true;
  axios.interceptors.response.use(
    (response) => {
      finishFunctionalRequest(response.config);
      if (isCloudApiRequest(response.config)) {
        markBackendAvailable();
        if (clearRecoveredCloudBlock() && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('ewu-operational-access-restored'));
        }
      }
      return response;
    },
    (error) => {
      finishFunctionalRequest(error.config);
      const wasBlocked = Boolean(activeStore?.state?.device?.blockedByDevicePolicy);
      const cloudRequest = isCloudApiRequest(error.config);
      if (cloudRequest && isOperationalBlockingError(error)) {
        const responseData = error.response?.data;
        const payload = responseData && typeof responseData === 'object'
          ? { ...responseData }
          : {};
        payload.__ewu_operational_request_id = error.config?.__ewuOperationalRequestId;
        activeStore?.dispatch('device/handleOperationalBlockingResponse', payload);
      } else if (cloudRequest && isBackendUnavailableError(error)) {
        markBackendUnavailable(
          error?.message || 'No se pudo conectar con Eat with Us.',
        );
        activeStore?.commit('device/setSecondaryApiUnavailable', error);
      }

      const isBlocked = Boolean(activeStore?.state?.device?.blockedByDevicePolicy);
      if (!wasBlocked && isBlocked && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ewu-operational-access-blocked'));
      }
      return Promise.reject(error);
    },
  );
}

installDeviceHeaderInterceptor();
